/**
 * Google Analytics 4 (gtag.js) integration for StartupNexus.
 *
 * GA4 web data stream:
 *   Stream name:    nexusaiweb-startupnexus
 *   Stream URL:     https://spn.nexusaiweb.com
 *   Stream ID:      14975783912
 *   Measurement ID: G-D29N4XFHNQ   (override via VITE_GA_MEASUREMENT_ID)
 *
 * GA only runs in PRODUCTION builds (import.meta.env.PROD) so local dev does
 * not pollute the analytics property. gtag.js is injected lazily on init; page
 * views are sent manually on route changes (SPA), so gtag's automatic page_view
 * is disabled to avoid double-counting.
 */

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-D29N4XFHNQ';

export const GA_ENABLED = import.meta.env.PROD && Boolean(GA_MEASUREMENT_ID);

let initialized = false;

// ── Meta (Facebook) Pixel ────────────────────────────────────────────────────
// index.html sends the PageView for the page a visitor lands on, so the first route-change call here is
// skipped (otherwise every visit counts twice). Later navigations inside the app send their own PageView.
let metaFirstView = true;
export function metaPageView() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  if (metaFirstView) { metaFirstView = false; return; }
  window.fbq('track', 'PageView');
}
const META_STANDARD = new Set(['Lead', 'CompleteRegistration', 'Purchase', 'InitiateCheckout', 'AddToCart', 'Subscribe', 'StartTrial', 'ViewContent', 'Search', 'Contact']);
export function metaTrack(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try { window.fbq(META_STANDARD.has(name) ? 'track' : 'trackCustom', name, params); } catch (e) { /* tracking must never break the app */ }
}
// The few GA events that are also Meta conversions. Everything else stays GA-only.
function mirrorToMeta(name, params) {
  if (name === 'sign_up') metaTrack('CompleteRegistration', { method: params && params.method });
  else if (name === 'generate_lead' || name === 'lead') metaTrack('Lead', { source: params && params.source });
  else if (name === 'purchase' && Number.isFinite(params && params.value)) metaTrack('Purchase', { currency: params.currency || 'INR', value: params.value });
}


/** Inject gtag.js and configure the GA4 stream. Safe to call multiple times. */
export function initGA() {
  if (!GA_ENABLED || initialized || typeof window === 'undefined') return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
}

/** Send a GA4 page_view for the given SPA path. */
export function trackPageView(path, title) {
  metaPageView();
  if (!GA_ENABLED || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title || document.title,
  });
}

/** Send a custom GA4 event, e.g. trackEvent('idea_submitted', { id }). */
export function trackEvent(name, params = {}) {
  mirrorToMeta(name, params);
  if (!GA_ENABLED || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', name, params);
}
